export type AiIdempotencyFlow = 'quick-task' | 'goal-to-tasks';

export interface AiIdempotencyStore {
  /**
   * Returns the stored key for a project/board/flow triple, generating a UUID on
   * first access so repeated apply calls reuse the same key.
   */
  getKey(projectId: string, boardId: string, flow: AiIdempotencyFlow): string;
  /**
   * Replaces the stored key with a fresh UUID. Called after each successful
   * propose so the next apply never reuses a key whose request already ran.
   */
  regenerate(projectId: string, boardId: string, flow: AiIdempotencyFlow): string;
}

/**
 * Factory kept pure so tests can create isolated stores. The singleton below
 * is the module-scope store that survives across dialog sessions.
 */
export function createAiIdempotencyStore(): AiIdempotencyStore {
  const keys = new Map<string, string>();

  function cacheKey(projectId: string, boardId: string, flow: AiIdempotencyFlow): string {
    return `${flow}:${projectId}/${boardId}`;
  }

  return {
    getKey(projectId, boardId, flow) {
      const key = cacheKey(projectId, boardId, flow);
      const existing = keys.get(key);
      if (existing) return existing;
      const generated = crypto.randomUUID();
      keys.set(key, generated);
      return generated;
    },

    regenerate(projectId, boardId, flow) {
      const key = cacheKey(projectId, boardId, flow);
      const generated = crypto.randomUUID();
      keys.set(key, generated);
      return generated;
    },
  };
}

/**
 * Module-scope idempotency store for the AI flows.
 * Keyed by flow + project/board so each flow has its own key lifecycle and
 * regenerating one flow never leaks into the other.
 */
export const aiIdempotencyStore = createAiIdempotencyStore();
